<template>
  <div class="a-breadcrumb" v-if="isLogin">
    <div class="r-container">
      <ul class="a-breadcrumb__content">
        <li v-for="(item, key) of matched" :key="key" class="breadcrumb__link">
          <router-link
            :to="item.path"
            :class="key === matched.length - 1 ? 'router-link-exact-active' : ''"
            class="text-sm w6"
          >
            {{ item.meta?.name ?? item.name }}
          </router-link>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { computed, watch, ref } from 'vue';
import { MY_BREADCRUMB } from '@/constants/common';
import {
  ESTATE_DETAIL_SELLER_BID_HISTORY,
  ESTATE_HISTORY,
  ESTATE_RE_EXHIBIT,
  ESTATE_RE_EXHIBIT_CONFIRM,
} from '@/constants/screen';

const route = useRoute();
const store = useStore();
const breadType = ref('default');

const estate = computed(() => {
  if (route.path?.includes('estate-detail-seller')) {
    return store.getters['estate/estateSellerDetailOrigin'];
  }
  if (route.path?.includes('estate-edit')) {
    return store.getters['estate/estateDetail'];
  }
  return store.getters['estate/estateOrigin'];
});

const onCreated = () => {
  if (route.path?.includes('estate-info')) {
    breadType.value = 'estate-info';
  } else if (route.path?.includes('estate-detail-seller')) {
    breadType.value = 'estate-detail-seller';
  } else {
    breadType.value = 'default';
  }
};
onCreated();

watch(
  () => route.path,
  () => {
    onCreated();
  }
);

const isLogin = computed(() => store.state.auth.isLogin);

const bidHistory = computed(() => {
  if (route.name === ESTATE_DETAIL_SELLER_BID_HISTORY) {
    return store.getters['bidding-history/dataBiddingHistorySeller'];
  }
  return store.getters['bidding-history/dataBiddingHistory'];
});

const news = computed(() => store.getters['news/newDetail']);

const matched = computed(() => {
  // eslint-disable-next-line prefer-const
  let breadcrumbData = JSON.parse(localStorage.getItem(MY_BREADCRUMB));

  if (breadcrumbData && breadType.value !== 'default') {
    // eslint-disable-next-line operator-linebreak
    let estateBreadcrumb = breadcrumbData.find((item) => item.type === breadType.value)?.data ?? [];

    // From Home
    if (estateBreadcrumb.length === 0) {
      const breadcrumbDefault = [
        {
          name: 'Home',
          path: '/home',
        },
        {
          name: estate.value?.estate_name ?? bidHistory.value.estate_name ?? '',
          path: route.path,
        },
      ];
      return breadcrumbDefault;
    }

    const checkRightEstate = estateBreadcrumb?.some(
      (item) => item.name === estate.value.estate_name
    );

    const checkRightHistory = estateBreadcrumb?.some((item) => item.name === '入札履歴');

    // For Estate Info 7x
    if (breadType.value === 'estate-info') {
      if (!checkRightEstate) {
        if (estateBreadcrumb[2]) {
          estateBreadcrumb[2].name = estate.value.estate_name;
          estateBreadcrumb[2].path = `/estate-info/estate-detail/${estate.value.id}`;
        } else {
          estateBreadcrumb[1].name = estate.value.estate_name;
          estateBreadcrumb[1].path = `/estate-info/estate-detail/${estate.value.id}`;
        }
      }
      if (route.path.includes('estate-bid-history') && !checkRightHistory) {
        const historyPath = `/estate-info/estate-bid-history/${estate.value.id}`;
        estateBreadcrumb.push({
          name: '入札履歴',
          path: historyPath,
        });
      }

      // remove history_crumb when back estate-info
      if (route.name !== ESTATE_HISTORY) {
        estateBreadcrumb = estateBreadcrumb.filter((item) => item.name !== '入札履歴');
      }
      const indexEstateRoute = breadcrumbData?.findIndex((item) => item.type === breadType.value);
      if (indexEstateRoute > -1) {
        breadcrumbData.splice(indexEstateRoute, 1);
      }
      breadcrumbData.push({
        type: breadType.value,
        data: estateBreadcrumb,
      });
      localStorage.setItem(MY_BREADCRUMB, JSON.stringify(breadcrumbData));
    }

    // For Estate Detail Seller 12x
    if (breadType.value === 'estate-detail-seller') {
      if (!checkRightEstate) {
        if (estateBreadcrumb[2]) {
          estateBreadcrumb[2].name = estate.value.estate_name;
          estateBreadcrumb[2].path = `/estate-detail-seller/${estate.value.id}`;
        } else {
          estateBreadcrumb[1].name = estate.value.estate_name;
          estateBreadcrumb[1].path = `/estate-detail-seller/${estate.value.id}`;
        }
      }

      if (route.path.includes('bid-history') && !checkRightHistory) {
        const historyPath = `/estate-detail-seller/bid-history/${estate.value.id}`;
        estateBreadcrumb.push({
          name: '入札履歴',
          path: historyPath,
        });
      }

      const checkRightRelist = estateBreadcrumb?.some((item) => item.name === '再出品');
      if (route.path.includes('re-exhibit') && !checkRightRelist) {
        const relistPath = `/estate-detail-seller/re-exhibit/${estate.value.id}`;
        estateBreadcrumb.push({
          name: '再出品',
          path: relistPath,
        });
      }

      const checkRightConfirm = estateBreadcrumb?.some((item) => item.name === '再出品確認');
      if (route.path.includes('confirm') && !checkRightConfirm) {
        const confirmPath = `/estate-detail-seller/re-exhibit/${estate.value.id}/confirm`;
        estateBreadcrumb.push({
          name: '再出品確認',
          path: confirmPath,
        });
      }

      if (route.name !== ESTATE_RE_EXHIBIT_CONFIRM) {
        estateBreadcrumb = estateBreadcrumb.filter((item) => item.name !== '再出品確認');
      }

      if (route.name !== ESTATE_RE_EXHIBIT && route.name !== ESTATE_RE_EXHIBIT_CONFIRM) {
        estateBreadcrumb = estateBreadcrumb.filter((item) => item.name !== '再出品');
      }

      // remove history_crumb when back estate-info
      if (route.name !== ESTATE_DETAIL_SELLER_BID_HISTORY) {
        estateBreadcrumb = estateBreadcrumb.filter((item) => item.name !== '入札履歴');
      }
      const indexEstateRoute = breadcrumbData?.findIndex((item) => item.type === breadType.value);
      if (indexEstateRoute > -1) {
        breadcrumbData.splice(indexEstateRoute, 1);
      }
      breadcrumbData.push({
        type: breadType.value,
        data: estateBreadcrumb,
      });
      localStorage.setItem(MY_BREADCRUMB, JSON.stringify(breadcrumbData));
    }

    return estateBreadcrumb;
  }

  // For News
  if (route.path.includes('news') && route.path.includes('detail')) {
    const breadcrumbNews = [
      {
        name: 'Home',
        path: '/home',
      },
      {
        name: 'ニュース',
        path: '/news',
      },
      {
        name: news.value?.news_title ?? '',
        path: `/news/${news.value?.id}/detail`,
      },
    ];
    return breadcrumbNews;
  }

  const defaultBreadcrumb = route.matched?.filter((item) => item.meta.name);

  if (route.path.includes('estate-edit')) {
    if (route.path.includes('violation-report')) {
      defaultBreadcrumb.splice(1, 0, {
        name: route.query.estateName,
        path: `/estate-info/estate-detail/${route.query.estateId}`,
      });
    } else {
      defaultBreadcrumb.splice(1, 0, {
        name: estate.value.estate_name,
        path: `/estate-detail-seller/${route.params.id || estate.value.id}`,
      });
    }
  }
  return defaultBreadcrumb;
});
</script>
